import sanitizeHtml from "sanitize-html";
import { HeaderPositionType } from "../constants";
import { BaseCategory } from "./BaseCategory";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";

export class TableCategory extends BaseCategory {
    get name() {
        return "Table";
    }

    get type() {
        return SlideCategories.TABLE;
    }

    get icon() {
        return null;
    }

    get description() {
        return "The content includes data that can be represented as a table. Also use for pricing tables.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Provide html for a table and break out the rows, columns, and cell data.`;
    }

    getImagePrompt() {
        return false;
    }

    getPromptSchema() {
        return {
            html: "string containing the HTML table representation",
            rows: [{
                row: "zero-based index",
                rowStyle: "plain | emphasized | header | section | summary",
            }],
            cols: [{
                col: "zero-based index",
                colStyle: "plain | emphasized | category",
            }],
            cells: [{
                row: "zero-based index",
                col: "zero-based index",
                text: "string",
                cellStyle: "plain | emphasized | header | section | summary",
            }],
            conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    validateGeneratedModel(model) {
        if (!this.validateArray(model.rows) || !this.validateArray(model.cols) || !this.validateArray(model.cells)) {
            return false;
        }
        return true;
    }

    getSlideModels(model) {
        let models = [
            this.buildTable(model)
        ];
        return models;
    }

    getRowStyle(style) {
        switch (style) {
            case "section":
                return "cleanRow";
            case "header":
                return "headerRow";
            case "summary":
                return "summaryRow";
            default:
                return "defaultRow";
        }
    }

    getColStyle(style) {
        switch (style) {
            case "header":
            case "category":
                return "headerCol";
            case "summary":
                return "summaryCol";
            case "emphasized":
                return "emphasizedCol";
            default:
                return "defaultCol";
        }
    }

    buildCell(model) {
        let value = sanitizeHtml(model.text, { allowedTags: [], allowedAttributes: {} });

        let cell = {
            row: model.row,
            col: model.col,
            cellText: { text: value },
            cellStyle: "text"
        };

        if (value.toLowerCase() == "yes" || value.toLowerCase() == "no" || value.toLowerCase() == "true" || value.toLowerCase() == "false") {
            cell.format = "icon";
            cell.content_type = "icon";
            cell.content_value = value.toLowerCase() == "yes" || value.toLowerCase() == "true" ? "check-yes" : "x";
            cell.cellColor = value.toLowerCase() == "yes" || value.toLowerCase() == "true" ? "positive" : "negative";
        }

        return cell;
    }

    buildTable(model) {
        let builder = new SlideModelBuilder("dashboard", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        let rows = model.rows ?? [];
        let maxRowFromCells = model.cells.reduce((max, cell) => Math.max(max, cell.row), -1);
        if (maxRowFromCells + 1 > rows.length) {
            // Add missing rows
            for (let i = rows.length; i <= maxRowFromCells; i++) {
                rows.push({ row: i, rowStyle: "defaultRow" });
            }
        }

        let cols = model.cols ?? [];
        let maxColFromCells = model.cells.reduce((max, cell) => Math.max(max, cell.col), -1);
        if (maxColFromCells + 1 > cols.length) {
            // Add missing cols
            for (let i = cols.length; i <= maxColFromCells; i++) {
                cols.push({ col: i, colStyle: "defaultCol" });
            }
        }

        builder.createPrimary({
            items: [{
                componentType: "Table",
                childElement: {
                    rows: rows.map((row, index) => ({
                        index,
                        style: this.getRowStyle(row.rowStyle),
                        size: row.rowStyle == "header" ? 20 : 50,
                    })),
                    cols: cols.map((col, index) => ({
                        index,
                        style: this.getColStyle(col.colStyle),
                    })),
                    cells: model.cells.map(cell => this.buildCell(cell)),
                }
            }],
        });
        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }
        return builder.model;
    }
}

